
import Vue from 'vue';
import XLSX from "xlsx";
import { RateCard, RateCardDetails } from "@/models/rate_card_models";
import axios from "axios";
import Papa from "papaparse";

const BASE_URL = (process.env.NODE_ENV === 'production') ? '/api' : '//localhost:8000';

const RATE_CARDS_URL = `${BASE_URL}/rate-cards/rate-cards`;

interface Rates {
  [prefix: string]: {
    // prices[0] is 1FP, prices[1] is 2FP etc.
    prices: number[]
    _count: number;
  }
}

class PostcodeRates {
  private rates: Rates = {};
  public hubCostPerPallet: number = 0;

  // prices[0] is 1FP, prices[1] is 2FP etc.
  public addRate(prefix: string, prices: number[]) {
    if (prefix in this.rates) {
      const rate = this.rates[prefix];
      rate.prices = rate.prices.map((p, i) => (p * rate._count + prices[i]) / (rate._count + 1))
      rate._count++;
    } else {
      this.rates[prefix] = {
        prices,
        _count: 1,
      }
    }
  }

  public getPrice(prefix: string, pallets: number): number {
    if (!(prefix in this.rates)) {
      return 0;
    }
    const prices = this.rates[prefix].prices;
    const i = Math.min(pallets, prices.length) - 1
    return prices[i] /*/ (i + 1)*/ * pallets;
  }

  public getRates(): (readonly [string, number[]])[] {
    return Object.entries(this.rates)
        .map(([prefix, price]) => [prefix, price.prices.map((p, i) => p + this.hubCostPerPallet * (i + 1))] as const)
        .sort((a, b) => a[0].localeCompare(b[0]));
  }
}

const rangeRE = /^([A-Z]+)?([0-9]+)?(?:-([0-9]+))?/
export default Vue.extend({
  name: "CustomerPortfolio",
  data() {
    return {
      postcodeRates: undefined as PostcodeRates | undefined,
      portfolio: [] as [string, ...number[]][],
      processing: false,
      accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx,text/csv,.csv",
      pricing: "network" as "network" | "customer",
      rateCards: [] as RateCard[],
      selectedRateCard: null as string | null,
      // Loaded from a CSV file
      postcodes: {} as { [prefix: string]: [string, ...number[]] },
    };
  },
  methods: {
    processRateCard(event: Event) {
      this.processing = true;
      const rates = new PostcodeRates();
      const reader = new FileReader();
      const rateCardFile = (event.target! as HTMLInputElement).files![0];
      reader.addEventListener("load", (e) => {
        const rateCard = XLSX.read(e.target!.result, { type: "buffer" });
        let sheet: XLSX.WorkSheet;
        let zone_column: number;
        let range_column: number;
        let first_rate_column: number;
        let last_rate_column: number;
        if ("RateCard-multi" in rateCard.Sheets) {
          sheet = rateCard.Sheets["RateCard-multi"];
          zone_column = 3;
          range_column = 2;
          first_rate_column = 4;
          last_rate_column = 7;
        } else {
          sheet = rateCard.Sheets["RateCard-single"];
          zone_column = 2;
          range_column = 1;
          first_rate_column = 4;
          last_rate_column = 4;
        }
        const rows: (string | undefined)[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        rows
            .filter((r) => r[zone_column]) // Only those rows which define zones
            .forEach((r) => {
              let prefix: string;
              const prices = r.slice(first_rate_column, last_rate_column + 1).map((x) => parseFloat(x!));
              for (const range of r[range_column]!.split(",").map((s) => s.trim())) {
                if (range.toLowerCase() === "local") {
                  continue;
                }
                const match = rangeRE.exec(range);
                if (match) {
                  if (match[1]) {
                    prefix = match[1];
                  } else {
                    continue
                  }
                  if (match[2]) {
                    // Single number or start of range
                    if (match[3]) {
                      // Range
                      for (let i = parseInt(match[2], 10); i <= parseInt(match[3], 10); ++i) {
                        rates.addRate(prefix, prices)
                      }
                    } else {
                      rates.addRate(prefix, prices)
                    }
                  } else {
                    // Just the prefix
                    rates.addRate(prefix, prices)
                  }
                }
              }
            });
        this.postcodeRates = rates;
        this.processing = false;
      });
      reader.readAsArrayBuffer(rateCardFile)
    },
    async processNetworkRateCard() {
      this.processing = true;
      try {
        const rates = new PostcodeRates();
        const response = await axios.get<RateCardDetails>(`${BASE_URL}/rate-cards/rate-card-details/${this.selectedRateCard}`,);
        const rateCard = response.data;
        rates.hubCostPerPallet = rateCard.hubCostPerPallet;
        const zoneCosts: { [zoneNumber: number]: number[] } = {}
        for (const imr of rateCard.interMemberCosts) {
          if (imr.palletType !== "Full" || imr.serviceLevel !== "Economy") {
            continue;
          }
          const zone = imr.zoneNumber;
          if (!(zone in zoneCosts)) {
            zoneCosts[zone] = [];
          }
          zoneCosts[zone][imr.minPallets - 1] = imr.cost;
        }
        for (const zone of rateCard.zones) {
          const costs = zoneCosts[zone.zoneNumber] ?? [];
          for (const p of zone.postcodes) {
            if (p.lower === null) {
              rates.addRate(p.prefix, costs);
            } else {
              for (let i = p.lower; i <= p.upper; ++i) {
                rates.addRate(p.prefix, costs);
              }
            }
          }
        }
        this.postcodeRates = rates;
        this.applyPostcodes();
      } finally {
        this.processing = false;
      }
    },
    processDeliveryVolumes(event: Event) {
      this.processing = true;
      this.portfolio = [];
      const vm = this;
      this.postcodes = {};
      const portfolioFile = (event.target! as HTMLInputElement).files![0];
      if (portfolioFile.name.endsWith(".csv")) {
        Papa.parse(portfolioFile, {
          complete(results: { data: string[][] }) {
            const postcodePrefixRe = /^\s*(?<prefix>[A-Z]+)/;
            for (const consignment of results.data.slice(3)) {
              if (consignment[0].trim().length === 0) {
                // End of data
                break;
              }
              const postcodePrefixResult = postcodePrefixRe.exec(consignment[15])
              if (!postcodePrefixResult) {
                continue;
              }
              const postcodePrefix = postcodePrefixResult?.groups?.prefix;
              if (!postcodePrefix) {
                continue;
              }
              if (!(postcodePrefix in vm.postcodes)) {
                vm.postcodes[postcodePrefix] = [
                  postcodePrefix,
                  0, // 1 BU
                  0, // 2 BU
                  0, // 3 BU
                  0, // 4 BU
                  0, // 5 BU
                  0, // 6 BU
                  0, // 7 BU
                  0, // Total Jobs
                  0, // Total Pallets
                  0, // Avg. Price per Pallet
                  0, // Total Price
                ];
              }
              const totalPallets = Math.min(parseInt(consignment[6]), 7);
              vm.postcodes[postcodePrefix][totalPallets] += 1;
              vm.postcodes[postcodePrefix][8] += 1;
              vm.postcodes[postcodePrefix][9] += totalPallets;
            }
            vm.applyPostcodes();
            vm.processing = false;
          }
        });
      } else {
        const reader = new FileReader();
        reader.addEventListener("load", (e) => {
          const portfolio = XLSX.read(e.target!.result, { type: "buffer" });
          const sheet = portfolio.Sheets[portfolio.SheetNames[0]];
          const rows: [string, ...number[]][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });
          this.portfolio = rows.slice(2);
          let grandTotalPallets = 0;
          let grandTotal = 0;
          for (const r of this.portfolio) {
            if (r[0].trim() === "Grand Total") {
              r.push(grandTotalPallets);
              r.push(grandTotal / grandTotalPallets);
              r.push(grandTotal)
            } else {
              const volumes = r.slice(1, 8) as number[];
              const pallets = volumes.reduce((pallets, jobs, i) => pallets + jobs * (i + 1), 0);
              grandTotalPallets += pallets;
              const price = volumes.reduce((price, jobs, i) => price + jobs * this.postcodeRates!.getPrice(r[0], i + 1), 0);
              grandTotal += price;
              r.push(pallets)
              r.push(price / pallets)
              r.push(price)
            }
          }
          this.processing = false;
        })
        reader.readAsArrayBuffer(portfolioFile)
      }
    },
    applyPostcodes() {
      this.portfolio = Object.values(this.postcodes);
      this.portfolio.sort((a, b) => a[0].localeCompare(b[0]));
      this.portfolio.forEach((r) => {
        const volumes = r.slice(1, 8) as number[];
        const price = volumes.reduce((price, jobs, i) => price + jobs * this.postcodeRates!.getPrice(r[0], i + 1), 0);
        r[11] = price;
        r[10] = r[11] / r[9]; // Avg. price per pallet = total price / total pallets
      })
    },
    downloadPrices() {
      const book = XLSX.utils.table_to_book(this.$refs.priceTable);
      XLSX.writeFile(book, "Pricing.xlsx", { compression: true });
    },
    download() {
      const book = XLSX.utils.table_to_book(this.$refs.portfolioTable);
      XLSX.writeFile(book, "Portfolio.xlsx", { compression: true });
    }
  },
  async mounted() {
    this.rateCards = (await axios.get<{ rateCards: RateCard[] }>(RATE_CARDS_URL)).data.rateCards;
  }
})
